import React, { useState } from "react";
import {
    Box,
    Container,
    HStack,
    Button,
    Text,
    IconButton,
    VStack,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_LOGIN_URL, APP_REGISTER_URL } from "../../../services/constants";
import { DoodleSparkle } from "../../CustomUi/DoodleIcons";

const PublicNavbar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    const brandColor = "brand.800";
    const brandColorLight = "brand.50";

    const handleNavigation = (sectionId: string) => {
        setMobileOpen(false);
        if (location.pathname === "/") {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        } else {
            window.location.href = `/#${sectionId}`;
        }
    };

    const navLinks: Array<{ label: string; path?: string; id?: string }> = [
        { label: "Features", path: "/features" },
        { label: "Demo", path: "/demo" },
        { label: "Pricing", path: "/pricing" },
        { label: "Affiliates", path: "/affiliates" },
        { label: "Blog", path: "/blog" },
        { label: "About", path: "/about" },
        { label: "Contact", path: "/contact" },
    ];

    return (
        <Box as="nav" py={3.5} bg="white" position="sticky" top={0} zIndex={100} borderBottom="2px solid" borderColor="brand.900" boxShadow="0 2px 0px rgba(10, 61, 46, 0.08)">
            <Container maxW="7xl">
                <HStack justify="space-between">
                    {/* Logo with doodle sparkle */}
                    <HStack gap={2} cursor="pointer" onClick={() => navigate("/")} position="relative">
                        <img src="/logo.svg" alt="Result" style={{ height: "30px" }} />
                        <Box position="absolute" top="-8px" right="-14px" className="doodle-wiggle">
                            <DoodleSparkle size={16} color="#F59E0B" />
                        </Box>
                    </HStack>

                    {/* Desktop nav links */}
                    <HStack gap={7} display={["none", "none", "flex"]}>
                        {navLinks.map((link) => (
                            <Text
                                key={link.label}
                                fontWeight="600"
                                fontSize="sm"
                                color="gray.700"
                                cursor="pointer"
                                transition="all 0.15s ease"
                                _hover={{ color: brandColor, transform: "translateY(-1px)" }}
                                onClick={() => link.path ? navigate(link.path) : handleNavigation(link.id!)}
                            >
                                {link.label}
                            </Text>
                        ))}
                    </HStack>

                    {/* Desktop CTA */}
                    <HStack gap={3} display={["none", "none", "flex"]}>
                        <Box
                            className="font-handwriting"
                            fontSize="md"
                            color="amber.700"
                            fontWeight="bold"
                            transform="rotate(-2deg)"
                            userSelect="none"
                            mr={1}
                        >
                            Free to try! ✏️
                        </Box>
                        <Button
                            variant="ghost"
                            size="sm"
                            fontWeight="bold"
                            color={brandColor}
                            borderRadius="xl"
                            _hover={{ bg: brandColorLight }}
                            onClick={() => (window.location.href = APP_LOGIN_URL)}
                        >
                            Sign In
                        </Button>
                        <Button
                            size="sm"
                            bg={brandColor}
                            color="white"
                            px={5}
                            fontWeight="bold"
                            borderRadius="xl"
                            border="2px solid #06231A"
                            boxShadow="2px 2px 0px #06231A"
                            transition="all 0.15s ease"
                            _hover={{ bg: "brand.900", transform: "translate(-1px, -1px)", boxShadow: "3px 3px 0px #06231A" }}
                            onClick={() => (window.location.href = APP_REGISTER_URL)}
                        >
                            Get Started
                        </Button>
                    </HStack>

                    {/* Mobile menu toggle */}
                    <IconButton
                        aria-label="Open menu"
                        variant="ghost"
                        display={["flex", "flex", "none"]}
                        onClick={() => setMobileOpen(!mobileOpen)}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {mobileOpen
                                ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
                                : <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></>
                            }
                        </svg>
                    </IconButton>
                </HStack>

                {/* Mobile menu */}
                {mobileOpen && (
                    <VStack align="stretch" pt={4} pb={2} gap={1} display={["flex", "flex", "none"]}>
                        {navLinks.map((link) => (
                            <Text
                                key={link.label}
                                py={2}
                                px={3}
                                fontWeight="medium"
                                fontSize="sm"
                                color="gray.700"
                                cursor="pointer"
                                borderRadius="md"
                                _hover={{ bg: brandColorLight, color: brandColor }}
                                onClick={() => link.path ? (setMobileOpen(false), navigate(link.path)) : handleNavigation(link.id!)}
                            >
                                {link.label}
                            </Text>
                        ))}
                        <Box pt={2} borderTop="1px" borderColor="gray.100" mt={1}>
                            <HStack gap={2} pt={2}>
                                <Button flex={1} variant="outline" size="sm" color={brandColor} borderColor={brandColor} onClick={() => (window.location.href = APP_LOGIN_URL)}>
                                    Sign In
                                </Button>
                                <Button flex={1} size="sm" bg={brandColor} color="white" _hover={{ bg: "brand.900" }} onClick={() => (window.location.href = APP_REGISTER_URL)}>
                                    Get Started
                                </Button>
                            </HStack>
                        </Box>
                    </VStack>
                )}
            </Container>
        </Box>
    );
};

export default PublicNavbar;
